// M145 — Mijn weekplan. Twee tabs: Deze week / Vorige week (rapport).
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { EmbedContext } from "@/contexts/EmbedContext";
import api from "@/lib/api";
import WeeklyPlanPage from "@/pages/WeeklyPlanPage";
import WeeklyReportPage from "@/pages/WeeklyReportPage";
import { ArrowRight, ExternalLink, Check, Undo2, Loader2 } from "lucide-react";

const TABS = [
  { key: "deze-week", label: "Deze week" },
  { key: "vorige-week", label: "Vorige week (rapport)" },
];

function TrackBadge({ track }) {
  const T = {
    seo: { bg: "#E8F5EF", fg: "#087F8C", label: "SEO" },
    cro: { bg: "#FBE9CE", fg: "#8A5B13", label: "CRO" },
    merkverhaal: { bg: "#F4B183", fg: "#172C32", label: "Merk" },
    brand: { bg: "#F4B183", fg: "#172C32", label: "Merk" },
  }[track?.toLowerCase() || "seo"] || { bg: "#E8F5EF", fg: "#087F8C", label: track || "SEO" };
  return (
    <span className="inline-block text-xs font-semibold rounded-full px-2.5 py-1"
           style={{ background: T.bg, color: T.fg, letterSpacing: "0.04em" }}>
      {T.label}
    </span>
  );
}

function AdviceRow({ a, index, onDone, onUndo }) {
  const nav = useNavigate();
  const url = a.page_url || "";
  const short = url.replace(/^https?:\/\//, "").slice(0, 60);
  return (
    <div className="bg-bg-panel rounded-[16px] p-5 shadow-card flex items-start gap-4"
          style={{ boxShadow: "0 1px 3px rgba(23,44,50,0.06)" }}
          data-testid={`weekplan-advice-${index}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          {url && (
            <a href={url} target="_blank" rel="noopener noreferrer"
                className="text-sm text-accent-teal-dim hover:underline inline-flex items-center gap-1 truncate">
              {short} <ExternalLink className="w-3 h-3 shrink-0" />
            </a>
          )}
          <TrackBadge track={a.track} />
        </div>
        <div className="mt-2 text-base text-text-primary font-medium">{a.what_change || a.title}</div>
        {a.location_human && (
          <div className="mt-1 text-sm text-text-muted italic">{a.location_human}</div>
        )}
        {(a.onderbouwing?.huidige_situatie || a.why) && (
          <div className="mt-2 text-sm text-text-muted">
            <span className="font-medium text-text-primary">Waarom dit werkt:</span>{" "}
            {a.onderbouwing?.huidige_situatie || a.why}
          </div>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => nav(`/adviezen?page=${a.page_id}&advice=${a.advice_id}`)}
            data-testid={`weekplan-advice-${index}-open`}
            className="inline-flex items-center gap-1 text-sm text-accent-teal-dim hover:text-accent-teal"
          >
            Bekijk in adviezen <ArrowRight className="w-3.5 h-3.5" />
          </button>
          {a.status === "measuring" ? (
            <button onClick={() => onUndo(a.advice_id)}
                     className="text-xs px-3 py-1.5 rounded-btn border border-border-hairline text-text-muted hover:bg-bg-canvas inline-flex items-center gap-1">
              <Undo2 className="w-3 h-3" /> Toch niet
            </button>
          ) : (
            <button onClick={() => onDone(a.advice_id)}
                     data-testid={`weekplan-advice-${index}-done`}
                     className="text-xs px-3 py-1.5 rounded-btn border border-pos-green/40 text-pos-green hover:bg-pos-green/10 inline-flex items-center gap-1">
              <Check className="w-3 h-3" /> Ik heb dit gedaan
            </button>
          )}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-xs text-text-muted">Kans</div>
        <div className="text-lg font-bold text-accent-teal tabular-nums">
          {a.opportunity_score ?? "—"}
        </div>
      </div>
    </div>
  );
}

function DezeWeek() {
  const nav = useNavigate();
  const [state, setState] = useState({ items: [], adsProposals: [], loaded: false, err: null });
  const [showAll, setShowAll] = useState(false);
  const load = async () => {
    // Laad adviezen + ads-voorstellen parallel; ads-voorstellen falen
    // netjes als endpoint (nog) niet reachable — dan tonen we alleen adviezen.
    let items = [];
    let adsProposals = [];
    let err = null;
    try {
      const { data } = await api.get("/advies/open?limit=100");
      items = data.items || [];
    } catch (_e) {
      try {
        const { data } = await api.get("/advies?status=open&limit=100");
        items = data.items || [];
      } catch (e) {
        err = "Kon adviezen niet laden.";
      }
    }
    try {
      const { data: adsData } = await api.get("/ads-proposals/gallery");
      adsProposals = (adsData.items || []).filter((p) => p.status === "open");
    } catch (_e) { /* stil — Ads-tab niet aanwezig is prima */ }
    setState({ items, adsProposals, loaded: true, err });
  };
  useEffect(() => { load(); }, []);
  const done = async (id) => {
    try { await api.post(`/advies/${id}/done`); await load(); } catch (_e) {}
  };
  const undo = async (id) => {
    try { await api.post(`/advies/${id}/undo`); await load(); } catch (_e) {}
  };

  if (!state.loaded) return (
    <div className="flex items-center gap-2 text-text-muted"><Loader2 className="w-4 h-4 animate-spin" /> Adviezen laden…</div>
  );

  const open = state.items.filter(i => (i.status || "open") !== "measured");
  const ads = state.adsProposals || [];
  const total = open.length + ads.length;
  const TOP = 5;
  // Ads tussen adviezen mengen — Ads bovenaan (meest urgent volgens root).
  const merged = [
    ...ads.map((p) => ({ __kind: "ads", ...p })),
    ...open.map((a) => ({ __kind: "advies", ...a })),
  ];
  const visible = showAll ? merged : merged.slice(0, TOP);
  const hiddenCount = Math.max(0, total - TOP);

  if (total === 0) {
    return (
      <div className="rounded-[16px] border border-dashed border-border-hairline bg-bg-panel p-8 text-center"
            data-testid="weekplan-empty">
        <p className="text-sm text-text-muted max-w-md mx-auto mb-4">
          Geen open kansen. Je bent bij.
        </p>
        <button onClick={() => nav("/resultaten")}
                 className="inline-flex items-center gap-2 rounded-[12px] px-5 py-2.5 border border-accent-teal text-accent-teal-dim hover:bg-accent-mint text-sm font-medium">
          Bekijk het effect van vorige adviezen <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  const topCount = Math.min(TOP, total);
  return (
    <div>
      <div className="rounded-[16px] p-6 bg-accent-mint mb-6" data-testid="weekplan-summary">
        <div className="text-base font-medium text-text-primary">
          Deze week {topCount} {topCount === 1 ? "topkans" : "topkansen"}.
          {total > TOP && ` In totaal ${total} open.`}
        </div>
        <div className="text-sm text-text-primary/70 mt-1">
          Rangschikking op wat het meest oplevert. Klik door om te lezen.
        </div>
      </div>
      <div className="space-y-4" data-testid="weekplan-list">
        {visible.map((item, i) => (
          item.__kind === "ads"
            ? <AdsRow key={item.proposal_id} p={item} index={i} nav={nav} />
            : <AdviceRow key={item.advice_id || i} a={item} index={i} onDone={done} onUndo={undo} />
        ))}
      </div>
      {hiddenCount > 0 && (
        <div className="mt-6 text-center">
          <button
            onClick={() => setShowAll(v => !v)}
            data-testid="weekplan-toggle-all"
            className="inline-flex items-center gap-2 rounded-[12px] px-5 py-2.5 border border-accent-teal text-accent-teal-dim hover:bg-accent-mint text-sm font-medium"
          >
            {showAll
              ? "Toon minder — beperk tot top 5"
              : `Toon alle ${total} open kansen`}
          </button>
        </div>
      )}
    </div>
  );
}

function AdsRow({ p, index, nav }) {
  return (
    <div className="bg-bg-panel rounded-[16px] p-5 shadow-card flex items-start gap-4"
          style={{ boxShadow: "0 1px 3px rgba(23,44,50,0.06)" }}
          data-testid={`weekplan-ads-${index}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="inline-block text-xs font-semibold rounded-full px-2.5 py-1"
                 style={{ background: "#E8F5EF", color: "#087F8C", letterSpacing: "0.04em" }}>
            Advertentie
          </span>
          <span className="inline-block text-xs font-medium rounded-full px-2.5 py-1"
                 style={{ background: "#F7F8F5", color: "#627478" }}>
            {(p.proposal_type || "").replace(/_/g, " ")}
          </span>
        </div>
        <div className="mt-2 text-base text-text-primary font-medium">{p.wat_wijzigen_nl}</div>
        {p.waar_in_ads_nl && (
          <div className="mt-1 text-sm text-text-muted italic">{p.waar_in_ads_nl}</div>
        )}
        {p.impact_schatting_nl && (
          <div className="mt-2 text-sm text-text-muted">
            <span className="font-medium text-text-primary">Impact:</span> {p.impact_schatting_nl}
          </div>
        )}
        <div className="mt-4">
          <button
            onClick={() => nav(`/content?tab=advertenties`)}
            data-testid={`weekplan-ads-${index}-open`}
            className="inline-flex items-center gap-1 text-sm text-accent-teal-dim hover:text-accent-teal"
          >
            Bekijk voorstel <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function VorigeWeek() {
  return (
    <EmbedContext.Provider value={{ embedded: true }}>
      <WeeklyReportPage />
    </EmbedContext.Provider>
  );
}

export default function MijnWeekplanPage() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") === "vorige-week" ? "vorige-week" : "deze-week";
  const setTab = (t) => { const n = new URLSearchParams(sp); n.set("tab", t); setSp(n); };

  return (
    <AppLayout>
      <div className="pt-2">
        <div className="text-xs font-semibold text-accent-teal" style={{ letterSpacing: "0.06em" }}>
          MIJN WEEK
        </div>
        <h1 className="mt-2 text-4xl md:text-5xl font-bold text-text-primary tracking-tight"
             data-testid="weekplan-title">
          Wat pak je deze week op?
        </h1>
        <p className="mt-3 text-lg text-text-muted max-w-2xl">
          Concrete kansen, gerangschikt op wat het meest oplevert.
        </p>
      </div>
      <div className="mt-8 border-b border-border-hairline flex gap-6" data-testid="weekplan-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                   data-testid={`weekplan-tab-${t.key}`}
                   className={`pb-3 -mb-px text-sm font-medium transition-colors ${
                     tab === t.key
                       ? "text-accent-teal border-b-2 border-accent-teal"
                       : "text-text-muted hover:text-text-primary"
                   }`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="mt-8">
        {tab === "deze-week" ? <DezeWeek /> : <VorigeWeek />}
      </div>
    </AppLayout>
  );
}
